<template>
  <popup-modal ref="popup">
    <h3>Ny bordbooking</h3>
    <p class="text-muted small mt-2 mb-3">
      En booking tatt over telefon eller i restauranten. {{ confirmation }}
      Plassene telles med i det som kan bookes på nett.
    </p>

    <div class="row g-3">
      <div class="col-6">
        <label class="form-label">Dato</label>
        <datepicker v-model="dateObj" :locale="nbLocale" inputFormat="dd.MM.yyyy" class="form-control"/>
      </div>
      <div class="col-6">
        <label class="form-label">Klokkeslett</label>
        <!-- The project's own picker: always 24-hour, on the location's slot grid. A native <input type=time>
             follows the browser's locale and turns into AM/PM on an English one. -->
        <div class="bo_booking-time">
          <TimePicker v-model="booking.time" :step-minutes="slotMinutes" :default-hour="18" />
        </div>
      </div>
      <div class="col-6">
        <label class="form-label">Antall gjester</label>
        <input type="number" class="form-control" min="1" max="200" v-model.number="booking.guests">
      </div>
      <div class="col-6">
        <label class="form-label">Navn</label>
        <input type="text" class="form-control" maxlength="80" v-model.trim="booking.name">
      </div>
      <div class="col-6">
        <label class="form-label">Mobilnummer</label>
        <input type="tel" class="form-control" v-model.trim="booking.phone">
      </div>
      <div class="col-6">
        <label class="form-label">E-post (valgfritt)</label>
        <input type="email" class="form-control" :class="{ 'is-invalid': emailInvalid }" v-model.trim="booking.email">
        <div v-if="emailInvalid" class="invalid-feedback">Sjekk e-postadressen, eller la feltet stå tomt.</div>
        <div v-else-if="noConfirmation" class="form-text">Uten e-post får gjesten ingen bekreftelse – SMS er slått av.</div>
      </div>
      <div class="col-12">
        <label class="form-label">Kommentar (valgfritt)</label>
        <input type="text" class="form-control" maxlength="500" placeholder="Allergier, barnestol, bursdag …" v-model.trim="booking.comment">
      </div>
    </div>

    <p v-if="errorMessage" class="text-danger mt-3 mb-0">{{ errorMessage }}</p>
    <p v-if="slotUnavailable" class="mt-2 mb-0">
      <button type="button" class="bo_btn_secondary" :disabled="saving" @click="save(true)">Legg inn likevel</button>
      <span class="text-muted small ms-2">Plassene er fulle etter innstillingene – dere kjenner lokalet best.</span>
    </p>

    <div class="mt-4 d-flex justify-content-between gap-2">
      <button type="button" class="bo_btn_secondary" :disabled="saving" @click="cancel">Avbryt</button>
      <button type="button" class="bo_btn" :disabled="saving || !complete" @click="save(false)">{{ saving ? 'Lagrer…' : 'Legg inn booking' }}</button>
    </div>
  </popup-modal>
</template>

<script>
import PopupModal from '@/components/other/PopupModal.vue'
import TimePicker from '@/components/other/TimePicker.vue'
import BookingService from '@/services/booking/BookingService'
import Datepicker from '@/components/other/ToggleDatepicker.vue'
import { nb } from 'date-fns/locale'
import moment from 'moment'

const MESSAGES = {
  INVALID_GUESTS: 'Antall gjester må være minst 1.',
  INVALID_DATE: 'Dato eller klokkeslett mangler, eller ligger for langt tilbake.',
  INVALID_NAME: 'Skriv inn navnet på gjesten.',
  INVALID_PHONE: 'Mobilnummeret ser ikke riktig ut.',
  INVALID_EMAIL: 'E-postadressen ser ikke riktig ut – rett den, eller la feltet stå tomt.',
  SLOT_UNAVAILABLE: 'Ikke ledig: dagen er stengt eller plassene er fulle på dette tidspunktet.',
  BUSY: 'En annen booking er på vei inn samtidig – prøv igjen.',
  LOCATION_NOT_FOUND: 'Fant ikke avdelingen.'
}

export default {
  name: 'BookingCreateModal',
  components: { PopupModal, Datepicker, TimePicker },
  emits: ['created'],
  data() {
    return {
      locationId: '',
      slotMinutes: 30,
      guestNotify: { sms: false, email: true },
      nbLocale: nb,
      booking: this.emptyBooking(),
      errorMessage: '',
      slotUnavailable: false,
      saving: false
    }
  },
  computed: {
    dateObj: {
      get() { return moment(this.booking.date, 'YYYY-MM-DD').toDate() },
      set(d) { this.booking.date = moment(d).format('YYYY-MM-DD') }
    },
    complete() {
      const b = this.booking
      return b.date && b.time && b.guests > 0 && b.name.length > 1 && b.phone.length > 5 && !this.emailInvalid
    },
    /** E-mail is optional here (a guest on the phone has heard the booking confirmed), but one typed in
     *  must be valid — the server refuses it otherwise. */
    emailInvalid() {
      const email = this.booking.email
      return email.length > 0 && !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email)
    },
    /** E-mail is the location's only channel and none was given: the guest hears nothing from us. */
    noConfirmation() {
      return !this.guestNotify.sms && this.guestNotify.email && !this.booking.email
    },
    /** What the guest will actually get for this booking: the location's channels, minus e-mail when
     *  there is no address — the server sends e-mail only to an address the booking has. */
    confirmation() {
      const sms = this.guestNotify.sms
      const email = this.guestNotify.email && this.booking.email.length > 0 && !this.emailInvalid
      if (sms && email) return 'Gjesten får bekreftelse på SMS og e-post.'
      if (sms) return 'Gjesten får bekreftelse på SMS.'
      if (email) return 'Gjesten får bekreftelse på e-post.'
      if (this.guestNotify.email) return 'Gjesten får bekreftelse på e-post hvis du fyller den inn.'
      return 'Gjesten varsles ikke automatisk – velg varsling under Innstillinger → Bordbooking.'
    }
  },
  methods: {
    emptyBooking() {
      return { date: moment().format('YYYY-MM-DD'), time: '', guests: 2, name: '', phone: '', email: '', comment: '' }
    },
    open(locationId, slotMinutes, date, guestNotify) {
      this.locationId = locationId
      this.slotMinutes = slotMinutes || 30
      this.guestNotify = guestNotify || { sms: false, email: true }
      this.booking = this.emptyBooking()
      if (date) this.booking.date = date
      this.errorMessage = ''
      this.slotUnavailable = false
      this.saving = false
      this.$refs.popup.open()
    },
    cancel() {
      this.$refs.popup.close()
    },
    save(force) {
      this.errorMessage = ''
      this.slotUnavailable = false
      this.saving = true
      BookingService.create(this.locationId, this.booking, force).then(
        (response) => {
          this.saving = false
          const result = response.data && response.data.result
          if (result === 'OK') {
            this.$refs.popup.close()
            this.$emit('created')
          } else {
            this.errorMessage = MESSAGES[result] || 'Kunne ikke legge inn bookingen (' + result + ').'
            this.slotUnavailable = result === 'SLOT_UNAVAILABLE'
          }
        },
        (error) => {
          console.log(error)
          this.saving = false
          this.errorMessage = 'Kunne ikke legge inn bookingen. Prøv igjen.'
        }
      )
    }
  }
}
</script>

<style scoped>
/* TimePicker is built for the opening-hours rows: an 80 px inline box. In a form it has to sit under
   its label and fill the column like the .form-control next to it. */
.bo_booking-time :deep(.time-picker) {
  display: block;
  width: 100%;
}
.bo_booking-time :deep(.time-picker-input) {
  width: 100%;
  padding: .375rem .75rem;
  font-size: 1rem;
  line-height: 1.5;
  text-align: left;
  color: inherit;
  border-radius: var(--bs-border-radius, .375rem);
}
</style>
